/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import type { AdUnitTransformerConfig } from '../../types';
import type { SiteConfig } from '../config';

type TransformerFn = (adUnitCode: string) => string;

const TRANSFORMERS: Record<string, (config: AdUnitTransformerConfig) => TransformerFn> = {
  dropLastSegment: (config) => {
    const delimiter = config.delimiter ?? '_';
    return (adUnitCode: string) => {
      const parts = adUnitCode.split(delimiter);
      if (parts.length <= 1) return adUnitCode;
      return parts.slice(0, -1).join(delimiter);
    };
  },
};

export function createTransformer(config: AdUnitTransformerConfig): TransformerFn {
  const factory = TRANSFORMERS[config.name];
  if (!factory) {
    throw new Error(`Unknown transformer: ${config.name}`);
  }
  return factory(config);
}

export function getTransformerFromConfig(
  siteConfig: SiteConfig,
  clientTransformer?: TransformerFn,
): TransformerFn {
  // Client-side transformer takes precedence
  if (clientTransformer) return clientTransformer;

  // Server-side transformer from PropertyConfig
  const transformerConfig = siteConfig.adUnitTransformerConfig;
  if (transformerConfig) {
    return createTransformer(transformerConfig);
  }

  // Default: identity function
  return (code) => code;
}
